// ══════════════════════════════════════════════════════════════════════════════
//   EMAIL TEMPLATES — the registration email, sent as a confirmation and again with the joining link.
//   Used by the Supabase Edge Functions to send real emails, and by the admin dashboard
//   to preview them, so what the administrator previews is exactly what is sent.
//
//   Keep this file self-contained (no imports): `npm run functions:sync` copies it into
//   supabase/functions/_shared/, where it has to work in Deno as well.
// ══════════════════════════════════════════════════════════════════════════════

export interface EmailWebinar {
  title: string;
  /** ISO timestamps */
  startAt: string;
  endAt: string;
  meetingPlatform?: string | null;
}

/** Where attendees send registration questions */
export interface EmailContact {
  email: string;
  phone: string;
}

export interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
}

export const DEFAULT_CONTACT: EmailContact = {
  email: "info@kolemedical.co.za",
  phone: "+27 64 540 6751",
};

const TZ = "Africa/Johannesburg";
const NAVY = "#024498";
const INK = "#343A40";
const MUTED = "#6C757D";

const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-ZA", { timeZone: TZ, weekday: "long", day: "numeric", month: "long", year: "numeric" });
const time = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-ZA", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false });

/** "Thursday, 12 November 2026, 18:00–19:30 SAST" */
export const emailWhen = (webinar: EmailWebinar) =>
  `${longDate(webinar.startAt)}, ${time(webinar.startAt)}–${time(webinar.endAt)} SAST`;

/** Every value placed in the HTML goes through this */
export const escapeHtml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

/** Only https links are ever put in a button */
export const isSafeMeetingLink = (link: string) => /^https:\/\/[^\s"'<>]+$/.test(link.trim());

const format = (webinar: EmailWebinar) => `Online${webinar.meetingPlatform ? ` via ${webinar.meetingPlatform}` : ""}`;

/** Shared frame: navy masthead, white card, footer. Table layout because email apps need it. */
function layout(preheader: string, body: string, footer: string) {
  return `<!doctype html>
<html lang="en">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>KOLE MEDICAL</title></head>
<body style="margin:0;padding:0;background:#F1F4F8;">
<span style="display:none;max-height:0;overflow:hidden;opacity:0;">${escapeHtml(preheader)}</span>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#F1F4F8;">
<tr><td align="center" style="padding:24px 12px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;background:#FFFFFF;border-radius:12px;overflow:hidden;font-family:'Segoe UI',Tahoma,Arial,sans-serif;color:${INK};">
<tr><td style="background:${NAVY};padding:20px 28px;color:#FFFFFF;font-size:18px;font-weight:700;letter-spacing:0.08em;">KOLE MEDICAL<span style="display:block;font-size:12px;font-weight:400;letter-spacing:0.02em;color:#D3E2F6;">Webinars &amp; Workshops</span></td></tr>
<tr><td style="padding:28px;font-size:16px;line-height:1.6;">${body}</td></tr>
<tr><td style="padding:18px 28px;background:#F8F9FA;font-size:12px;line-height:1.5;color:${MUTED};">${footer}</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

function detailsTable(rows: [string, string][]) {
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:20px 0;border:1px solid #DDE3EA;border-radius:8px;">
${rows.map(([label, value]) => `<tr><td style="padding:10px 14px;width:34%;font-size:14px;color:${MUTED};vertical-align:top;">${escapeHtml(label)}</td><td style="padding:10px 14px;font-size:15px;font-weight:600;">${escapeHtml(value)}</td></tr>`).join("\n")}
</table>`;
}

const contactHtml = (contact: EmailContact) =>
  `<p style="margin:20px 0 0;font-size:14px;color:${MUTED};">Questions about your registration? Email <a href="mailto:${escapeHtml(contact.email)}" style="color:${NAVY};">${escapeHtml(contact.email)}</a> or call ${escapeHtml(contact.phone)}, quoting your reference.</p>`;

const contactText = (contact: EmailContact) =>
  `Questions about your registration? Email ${contact.email} or call ${contact.phone}, quoting your reference.`;

const FOOTER_TEXT =
  "You are receiving this because you registered for a KOLE MEDICAL webinar. It is a message about your registration, not marketing.";

// ══════════════════════════════════════════════════════════════════════════════
//   THE REGISTRATION EMAIL — one email, sent twice:
//   1. Confirmation: automatically, straight after registering. It says the registration
//      was successful and has no joining link.
//   2. Joining instructions: when the administrator presses Send in the dashboard. The same
//      email again, with the joining link (Microsoft Teams or any other https:// video link)
//      added. It has its own subject line so attendees don't mistake it for a duplicate.
// ══════════════════════════════════════════════════════════════════════════════

export interface ConfirmationEmailData {
  firstName: string;
  reference: string;
  webinar: EmailWebinar;
  contact?: EmailContact;
}

export interface JoiningEmailData extends ConfirmationEmailData {
  meetingLink: string;
}

const JOIN_TIPS = [
  "Join a few minutes early. If there's a lobby or waiting room, the host will let you in.",
  "Use your registered name so the host can recognise you.",
  "This link is for registered attendees. Please don't share it.",
];

function registrationEmail(
  { firstName, reference, webinar, contact = DEFAULT_CONTACT }: ConfirmationEmailData,
  meetingLink?: string,
): RenderedEmail {
  const link = meetingLink?.trim();
  const joinLabel = webinar.meetingPlatform ? `Join on ${webinar.meetingPlatform}` : "Join the webinar";
  const rows: [string, string][] = [
    ["Webinar", webinar.title],
    ["When", emailWhen(webinar)],
    ["Format", format(webinar)],
    ["Reference", reference],
  ];
  const intro = "Your registration was successful. Thank you for registering for a KOLE MEDICAL webinar.";
  const nextSteps = "We will email your joining link to this address before the webinar starts. Please keep your reference number.";
  const cancelNote = "If you can no longer attend, reply to this email so we can offer your place to someone else.";

  const subject = link ? `Your joining link: ${webinar.title}` : `Registration confirmed: ${webinar.title}`;
  const preheader = link ? `Your link to join ${webinar.title}.` : `Your registration for ${webinar.title} was successful.`;

  const joinHtml = link
    ? `<p style="margin:0 0 14px;">Here is your link to join the webinar.</p>
<p style="margin:20px 0;text-align:center;"><a href="${escapeHtml(link)}" style="display:inline-block;padding:14px 28px;border-radius:999px;background:${NAVY};color:#FFFFFF;font-weight:700;text-decoration:none;">${escapeHtml(joinLabel)}</a></p>
<p style="margin:0 0 14px;font-size:14px;color:${MUTED};">If the button doesn't work, copy this link into your browser:<br><a href="${escapeHtml(link)}" style="color:${NAVY};word-break:break-all;">${escapeHtml(link)}</a></p>`
    : "";

  const afterDetailsHtml = link
    ? `<p style="margin:0 0 6px;font-weight:700;">Before you join</p>
<ul style="margin:0 0 14px;padding-left:20px;">
${JOIN_TIPS.map(tip => `<li>${escapeHtml(tip)}</li>`).join("\n")}
</ul>`
    : `<p style="margin:0 0 6px;font-weight:700;">What happens next</p>
<p style="margin:0 0 14px;">${escapeHtml(nextSteps)}</p>`;

  const html = layout(
    preheader,
    `<p style="margin:0 0 14px;">Hi ${escapeHtml(firstName)},</p>
<p style="margin:0 0 14px;">${escapeHtml(intro)}</p>
${joinHtml}
${detailsTable(rows)}
${afterDetailsHtml}
<p style="margin:0;">${escapeHtml(cancelNote)}</p>
${contactHtml(contact)}`,
    escapeHtml(FOOTER_TEXT),
  );

  const text = [
    `Hi ${firstName},`,
    "",
    intro,
    "",
    ...(link ? [`${joinLabel}: ${link}`, ""] : []),
    ...rows.map(([label, value]) => `${label}: ${value}`),
    "",
    ...(link
      ? ["Before you join", ...JOIN_TIPS.map(tip => `- ${tip}`)]
      : ["What happens next", nextSteps]),
    "",
    cancelNote,
    "",
    contactText(contact),
    "",
    "KOLE MEDICAL",
    FOOTER_TEXT,
  ].join("\n");

  return { subject, html, text };
}

/** Sent automatically after a registration is saved: no joining link */
export const confirmationEmail = (data: ConfirmationEmailData): RenderedEmail => registrationEmail(data);

/** The confirmation again, with the joining link. Throws if the link isn't a full https:// address */
export function joiningInstructionsEmail({ meetingLink, ...data }: JoiningEmailData): RenderedEmail {
  if (!isSafeMeetingLink(meetingLink)) throw new Error("The joining link must be a full https:// address.");
  return registrationEmail(data, meetingLink);
}
